import {useEffect, useMemo, useState} from "react";
import projectUtils from "../../../../utils/project-utils.js";
import {getAttendanceType, getGarrisonDispatchType, getGarrisonType, getTaskType} from "../utils/dashboard-filters.js";
import {
    getDashboardDragDropRule,
    isDashboardDragDropAllowed,
    normalizeDragDropValue,
} from "../utils/dashboard-drag-drop.js";

const cardKey = card => `${card.type}:${card.id}`;
const toId = value => value === null || value === undefined || value === "" ? null : String(value);

const getAttendanceState = attendance => getAttendanceType(attendance);
const getTaskState = task => getTaskType(task);

const getAttendanceCard = attendance => ({
    type: "attendance",
    id: attendance.attendanceId,
    state: getAttendanceState(attendance),
    item: attendance,
});

const getTaskCard = task => ({
    type: "task",
    id: task.id,
    state: getTaskState(task),
    item: task,
});

const getGarrisonCard = garrison => ({
    type: "garrison",
    id: garrison.garrisonId,
    state: getGarrisonType(garrison),
    item: garrison,
});

const getDispatchGroupId = garrison => {
    const type = getGarrisonDispatchType(garrison);
    if (type === "dispatchSupport") return toId(garrison.supportForGarrisonDispatchId);
    if (type === "dispatch") return toId(garrison.dispatchId);
    if (type === "dispatchActivity") return toId(garrison.dispatchId);
    return null;
};

const buildCardKeySet = cards => new Set(cards.map(cardKey).filter(Boolean));

const GARRISON_RELATABLE_STATES = new Set(["inProgress", "assignment", "panic"]);
const isGarrisonRelatableAttendance = attendance => GARRISON_RELATABLE_STATES.has(getAttendanceState(attendance));
const isGarrisonRelatableTask = task => GARRISON_RELATABLE_STATES.has(getTaskState(task));

const getRelatedGarrisonCards = (garrison, data) => {
    const groupId = getDispatchGroupId(garrison);
    const garrisonId = toId(garrison.garrisonId);
    const related = [];

    if (groupId) {
        data.garrisons
            .filter(item => item.garrisonId !== garrison.garrisonId && getDispatchGroupId(item) === groupId)
            .forEach(item => related.push(getGarrisonCard(item)));
    }

    data.attendances
        .filter(item => isGarrisonRelatableAttendance(item) && ((groupId && toId(item.dispatchId) === groupId) || (garrisonId && toId(item.garrisonId) === garrisonId)))
        .forEach(item => related.push(getAttendanceCard(item)));

    data.tasks
        .filter(item => isGarrisonRelatableTask(item) && garrisonId && toId(item.garrisonId) === garrisonId)
        .forEach(item => related.push(getTaskCard(item)));

    return related;
};

const getRelatedAttendanceCards = (attendance, data) => {
    const dispatchId = toId(attendance.dispatchId);
    const garrisonId = toId(attendance.garrisonId);
    const related = [];

    if (isGarrisonRelatableAttendance(attendance)) {
        data.garrisons
            .filter(item => {
                const groupId = getDispatchGroupId(item);
                return (dispatchId && groupId === dispatchId) || (garrisonId && toId(item.garrisonId) === garrisonId);
            })
            .forEach(item => related.push(getGarrisonCard(item)));
    }

    data.tasks
        .filter(item => garrisonId && toId(item.garrisonId) === garrisonId)
        .forEach(item => related.push(getTaskCard(item)));

    return related;
};

const getRelatedTaskCards = (task, data) => {
    const garrisonId = toId(task.garrisonId);
    if (!garrisonId) return [];

    const related = [];

    if (isGarrisonRelatableTask(task)) {
        data.garrisons
            .filter(item => toId(item.garrisonId) === garrisonId || getDispatchGroupId(item) === garrisonId)
            .forEach(item => related.push(getGarrisonCard(item)));
    }

    data.tasks
        .filter(item => toId(item.garrisonId) === garrisonId && toId(item.id) !== toId(task.id))
        .forEach(item => related.push(getTaskCard(item)));

    return related;
};

const getRelatedCards = (card, data) => {
    if (card.type === "garrison") return getRelatedGarrisonCards(card.item, data);
    if (card.type === "attendance") return getRelatedAttendanceCards(card.item, data);
    if (card.type === "task") return getRelatedTaskCards(card.item, data);
    return [];
};

const FOCUS_TARGET_TYPES = {
    attendance: ["garrison"],
    task: ["garrison"],
    garrison: ["attendance", "task"],
};

const CARD_ELEMENT_ID = {
    attendance: id => `attendancePanel_${id}`,
    garrison: id => `garrisonPanel_${id}`,
    task: id => `taskPanel_${id}`,
};

const getCardElement = card => {
    if (!card || typeof document === "undefined") return null;
    return document.getElementById(CARD_ELEMENT_ID[card.type](card.id));
};

const pickFocusTarget = (related, targetType) => {
    const targets = related.filter(item => item.type === targetType);
    return targets.find(item => item.state === "inProgress") ?? targets[0] ?? null;
};

const focusRelatedCard = (card, data) => {
    const targetTypes = FOCUS_TARGET_TYPES[card.type];
    if (!targetTypes) return;

    const related = getRelatedCards(card, data);

    requestAnimationFrame(() => {
        for (const targetType of targetTypes) {
            const element = getCardElement(pickFocusTarget(related, targetType));
            if (element) {
                element.scrollIntoView({behavior: "smooth", block: "nearest", inline: "nearest"});
                return;
            }
        }
    });
};

const buildDropPayload = (source, target, rule) => ({
    action: rule.action,
    garrisonId: source.type === "garrison" ? source.id : target.type === "garrison" ? target.id : null,
    attendanceId: source.type === "attendance" ? source.id : target.type === "attendance" ? target.id : null,
    taskId: source.type === "task" ? source.id : target.type === "task" ? target.id : null,
    garrison: source.type === "garrison" ? source.item : target.type === "garrison" ? target.item : null,
    attendance: source.type === "attendance" ? source.item : target.type === "attendance" ? target.item : null,
    task: source.type === "task" ? source.item : target.type === "task" ? target.item : null,
    source,
    target,
});

const isDispatchDrop = payload => payload.action === "dispatch"
    && Boolean(payload.garrisonId)
    && Boolean(payload.attendanceId);

const getGarrisonCardFromPayload = payload => {
    if (payload?.source?.type === "garrison") return payload.source;
    if (payload?.target?.type === "garrison") return payload.target;
    return null;
};

const useDashboardCardInteractions = ({
    data,
    permissions,
    onDraggingChange,
    onStartDispatch,
    onStartSupport,
    onQueueAttendance,
    onChoice,
    onTaskStart,
    onTaskAssignment,
    onTaskChoice,
}) => {
    const [selectedCards, setSelectedCards] = useState({primary: null, related: new Set()});
    const [contextMenu, setContextMenu] = useState(null);
    const [dragState, setDragState] = useState({source: null, allowedTargets: new Set(), hoverTarget: null});

    const allCards = useMemo(() => [
        ...(data.attendances ?? []).map(getAttendanceCard),
        ...(data.tasks ?? []).map(getTaskCard),
        ...(data.garrisons ?? []).map(getGarrisonCard),
    ], [data.attendances, data.garrisons, data.tasks]);
    const allCardKeys = useMemo(() => buildCardKeySet(allCards), [allCards]);

    useEffect(() => {
        setSelectedCards(current => {
            if (!current.primary) return current;
            if (!allCardKeys.has(current.primary)) return {primary: null, related: new Set()};

            const related = new Set([...current.related].filter(key => allCardKeys.has(key)));
            if (related.size === current.related.size) return current;

            return {primary: current.primary, related};
        });
    }, [allCardKeys]);

    const selectedCard = useMemo(
        () => allCards.find(card => cardKey(card) === selectedCards.primary) ?? null,
        [allCards, selectedCards.primary]
    );

    const getAllowedTargetKeys = source => buildCardKeySet(allCards.filter(target => {
        if (cardKey(source) === cardKey(target)) return false;

        const rule = getDashboardDragDropRule(source, target);
        if (["dispatch", "support"].includes(rule?.action) && !permissions.garrison) return false;
        return isDashboardDragDropAllowed(rule, {source, target, garrisons: data.garrisons});
    }));

    const clearDragState = () => {
        setDragState({source: null, allowedTargets: new Set(), hoverTarget: null});
        onDraggingChange(false);
    };

    const selectCardAsPrimary = card => {
        const primary = cardKey(card);

        setSelectedCards({
            primary,
            related: buildCardKeySet(getRelatedCards(card, data)),
        });
    };

    const selectCard = card => {
        const primary = cardKey(card);
        const isDeselecting = selectedCards.primary === primary;
        setContextMenu(null);

        setSelectedCards(current => {
            if (current.primary === primary) return {primary: null, related: new Set()};

            return {
                primary,
                related: buildCardKeySet(getRelatedCards(card, data)),
            };
        });

        if (!isDeselecting) focusRelatedCard(card, data);
    };

    const openContextMenu = (event, card) => {
        event.preventDefault();
        event.stopPropagation();
        selectCardAsPrimary(card);
        setContextMenu({
            card,
            x: event.clientX,
            y: event.clientY,
        });
    };

    const closeContextMenu = () => setContextMenu(null);

    const clearSelection = () => {
        setSelectedCards({primary: null, related: new Set()});
        setContextMenu(null);
    };

    const getSelectionClassName = card => {
        const key = cardKey(card);
        if (selectedCards.primary === key || selectedCards.related.has(key)) return "selected-drop-area-border";
        if (selectedCards.primary) return "not-selected-opacity";
        return "";
    };

    const getDropClassName = card => {
        const key = cardKey(card);
        if (!dragState.allowedTargets.has(key)) return "";
        if (dragState.hoverTarget === key) return "drop-area-border-hover";
        return "drop-area-border";
    };

    const handleDragStart = (event, card) => {
        const allowedTargets = getAllowedTargetKeys(card);
        const serializedCard = JSON.stringify({type: card.type, id: card.id, state: card.state});

        if (card.type === "garrison") selectCardAsPrimary(card);
        setContextMenu(null);
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("application/json", serializedCard);
        event.dataTransfer.setData("text/plain", serializedCard);

        setDragState({source: card, allowedTargets, hoverTarget: null});
        onDraggingChange(true);
    };

    const handleDragOver = (event, card) => {
        const key = cardKey(card);
        if (!dragState.allowedTargets.has(key)) return;

        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        setDragState(current => current.hoverTarget === key ? current : {...current, hoverTarget: key});
    };

    const handleDragLeave = card => {
        const key = cardKey(card);
        setDragState(current => current.hoverTarget === key ? {...current, hoverTarget: null} : current);
    };

    const handleDrop = (event, target) => {
        event.preventDefault();

        const source = dragState.source;
        const rule = getDashboardDragDropRule(source, target);

        if (!source || !isDashboardDragDropAllowed(rule, {source, target, garrisons: data.garrisons})) {
            clearDragState();
            projectUtils.swalErrorMessage("", "Essa combinação de arraste não é permitida.", "Ok");
            return;
        }

        const payload = buildDropPayload(source, target, rule);
        if (isDispatchDrop(payload)) {
            onStartDispatch(payload);
            clearDragState();
            return;
        }

        if (payload.action === "queue" && payload.attendanceId) {
            onQueueAttendance(payload);
            clearDragState();
            return;
        }

        if (payload.action === "choice" && payload.attendanceId) {
            onChoice(payload);
            clearDragState();
            return;
        }

        if (payload.action === "support" && payload.garrisonId && payload.target.type === "garrison") {
            onStartSupport({
                ...payload,
                supportForGarrisonId: payload.target.id,
                supportGarrison: payload.target.item,
            });
            clearDragState();
            return;
        }

        if (payload.action === "task" && payload.taskId) {
            const garrisonCard = getGarrisonCardFromPayload(payload);
            const garrisonState = normalizeDragDropValue(garrisonCard?.state);

            if (garrisonState === "dispatchactivity") {
                onTaskChoice({...payload, mode: "choice-task"});
                clearDragState();
                return;
            }

            if (garrisonState === "available") {
                onTaskStart({...payload, mode: "start-task"});
                clearDragState();
                return;
            }

            onTaskAssignment({...payload, mode: "assignment-task"});
            clearDragState();
            return;
        }

        projectUtils.swalErrorMessage("", "Essa ação de arraste ainda não possui fluxo disponível para este contexto.", "Ok");
        clearDragState();
    };

    return {
        clearSelection,
        closeContextMenu,
        contextMenu,
        getDropClassName,
        getSelectionClassName,
        handleDragEnd: clearDragState,
        handleDragLeave,
        handleDragOver,
        handleDragStart,
        handleDrop,
        openContextMenu,
        selectCard,
        selectedCard,
    };
};

export default useDashboardCardInteractions;
